/**
 * AskAnalyticsButton - Sprint 23 P1, Sprint 24+ Fix
 * 
 * Context-aware analytics button that opens Q&A modal IN PLACE
 * with pre-populated filters based on current page context.
 * 
 * IMPORTANT: 
 * - Uses <button type="button"> for trigger (no navigation)
 * - Opens modal on same page with context chips
 * - Navigation only when user selects a chart inside Q&A
 * - RBAC failures show inline warning, no redirect
 * 
 * Usage:
 * <AskAnalyticsButton 
 *   context="executive_portfolio" 
 *   filters={{ portfolio_id: 'abc', date_range: '30d' }}
 *   entryPoint="portfolio_header"
 * />
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { 
  Sparkles, ExternalLink, RefreshCw, Send, AlertTriangle,
  BarChart3, TrendingUp, PieChart, LineChart, X
} from 'lucide-react';
import { api } from '../api/client';
import { toast } from 'sonner';

// Context configurations
const CONTEXT_CONFIG = {
  executive_hub: {
    label: 'Executive Hub Analytics',
    description: 'Ask strategic questions about organizational health, portfolios, and governance',
    suggestedQueries: [
      'Which portfolios are at risk?',
      'What requires intervention this week?',
      'Which benefits are underperforming?',
      'Why has Outcome Achievement declined?',
      'Which programs are most constrained?',
      'What is causing Intent Alignment degradation?',
      'Which governance actions are overdue?'
    ]
  },
  executive_portfolio: {
    label: 'Portfolio Analytics',
    description: 'Ask questions about portfolio performance, budget, and roadmap',
    suggestedQueries: [
      'What is the overall portfolio health?',
      'Which programs are at risk?',
      'Show budget utilization trend',
      'What are the top bottlenecks?'
    ]
  },
  executive_dashboard: {
    label: 'Executive Analytics',
    description: 'Ask questions about company-wide metrics and strategic KPIs',
    suggestedQueries: [
      'What is the approval rate trend?',
      'Show SLA compliance by program',
      'Which projects need attention?',
      'Forecast delivery dates'
    ]
  },
  pmo_dashboard: {
    label: 'PMO Analytics',
    description: 'Ask questions about program management and delivery metrics',
    suggestedQueries: [
      'What is the overall program health?',
      'Show epic progress by program',
      'Which milestones are at risk?',
      'Resource capacity overview'
    ]
  },
  pmc: {
    label: 'Project Analytics',
    description: 'Ask questions about this project\'s health, timeline, and resources',
    suggestedQueries: [
      'What is the project health status?',
      'Show sprint velocity trend',
      'What are the blocking issues?',
      'Forecast completion date'
    ]
  },
  analytics_dashboard: {
    label: 'Analytics Hub',
    description: 'Explore all analytics and dashboards',
    suggestedQueries: [
      'Show portfolio overview',
      'Compare program performance',
      'Budget vs actuals by quarter',
      'Resource allocation heatmap'
    ]
  }
};

// Deep link chart options
const CHART_OPTIONS = [
  { id: 'budget_trend', label: 'Budget Trend', icon: TrendingUp, path: '/analytics/budget' },
  { id: 'sla_by_program', label: 'SLA by Program', icon: BarChart3, path: '/analytics/sla' },
  { id: 'portfolio_health', label: 'Portfolio Health', icon: PieChart, path: '/analytics/health' },
  { id: 'velocity_trend', label: 'Velocity Trend', icon: LineChart, path: '/analytics/velocity' },
];

const AskAnalyticsButton = ({ 
  context = 'executive_portfolio', 
  filters = {},
  variant = 'default', // 'default' | 'compact' | 'pill'
  entryPoint = 'unknown', // Entry point for audit tracking
  suggestedQueries = null, // Optional override for suggested queries
  className = '',
  'data-testid': dataTestId = 'ask-analytics-btn' // Allow testid override
}) => {
  const navigate = useNavigate();
  const [showDialog, setShowDialog] = useState(false);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [rbacError, setRbacError] = useState(null);
  const [loadingMessage, setLoadingMessage] = useState('Analyzing your question...');
  const [errorState, setErrorState] = useState(null);
  
  const config = CONTEXT_CONFIG[context] || CONTEXT_CONFIG.executive_portfolio;
  // Allow prop override for suggested queries
  const effectiveSuggestedQueries = suggestedQueries || config.suggestedQueries;
  
  // Build context defensively
  const buildContext = useCallback(() => {
    return {
      portfolioId: filters.portfolio_id || filters.portfolioId || null,
      programId: filters.program_id || filters.programId || null,
      projectId: filters.project_id || filters.projectId || null,
      dateRange: filters.date_range || filters.dateRange || null,
      stage: filters.stage || null,
      location: context,
      entryPoint
    };
  }, [filters, context, entryPoint]);
  
  // Build context chips from filters
  const contextChips = Object.entries(filters)
    .filter(([_, v]) => v)
    .map(([key, value]) => {
      const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      return { key, label: `${label}: ${value}` };
    });

  /**
   * Handle button click - opens modal in place, NO navigation
   */
  const handleOpen = useCallback(async (e) => {
    // CRITICAL: Prevent any navigation or event bubbling
    e.preventDefault();
    e.stopPropagation();
    
    // Clear any previous state
    setRbacError(null);
    setResponse(null);
    setQuery('');
    
    const contextData = buildContext();
    
    // Audit log with entry point - fire and forget
    api.post('/audit/log', {
      action: 'ask_analytics',
      context: contextData,
      opened_from: entryPoint
    }).then(res => {
      console.debug('Analytics audit logged:', res.data?.id);
    }).catch(err => {
      // Silent fail for audit logging - doesn't block UI
      console.debug('Audit log failed:', err?.message);
    });
    
    // Open the modal - NO NAVIGATION
    setShowDialog(true);
  }, [buildContext, entryPoint]);

  /**
   * Handle query submission with timeout handling
   */
  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (!query.trim()) return;
    
    setLoading(true);
    setRbacError(null);
    setErrorState(null);
    setResponse(null);
    setLoadingMessage('Analyzing your question...');
    
    // Start timeout timers
    const stillWorkingTimer = setTimeout(() => {
      setLoadingMessage('Still working on your answer...');
    }, 10000);
    
    const timeoutTimer = setTimeout(() => {
      setLoading(false);
      setErrorState({
        type: 'timeout',
        message: 'Request timed out. The service is taking longer than expected.'
      });
    }, 25000);
    
    const startTime = Date.now();
    
    try {
      const res = await api.post('/analytics/qa', {
        query,
        context: buildContext()  // Send context as object, not string
      }, { timeout: 20000 }); // 20 second timeout
      
      clearTimeout(stillWorkingTimer);
      clearTimeout(timeoutTimer);
      
      const latencyMs = Date.now() - startTime;
      
      if (res.data.error?.type === 'rbac') {
        // RBAC failure - show inline, no redirect
        setRbacError(res.data.error.message || 'You do not have permission to access this analytics data.');
        setResponse(null);
      } else {
        setResponse(res.data);
        setRbacError(null);
        
        // Log telemetry
        api.post('/telemetry/event', {
          event: 'qa_success',
          success: true,
          latency_ms: latencyMs,
          suggestion_type: res.data.ai_generated ? 'llm' : 'mock',
          extra: { context, entryPoint }
        }).catch(() => {});
      }
    } catch (error) {
      clearTimeout(stillWorkingTimer);
      clearTimeout(timeoutTimer);
      
      console.error('Analytics Q&A failed:', error);
      
      const latencyMs = Date.now() - startTime;
      
      // Log error telemetry
      api.post('/telemetry/event', {
        event: 'qa_error',
        success: false,
        latency_ms: latencyMs,
        extra: { context, entryPoint, error_code: error.response?.status || 'network' }
      }).catch(() => {});
      
      // Check for RBAC error in response
      if (error.response?.status === 403) {
        setRbacError('You do not have permission to access this analytics data. Contact your administrator.');
        setResponse(null);
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        setErrorState({
          type: 'timeout',
          message: 'Request timed out. Please try again.'
        });
      } else {
        // Show error state with retry option
        setErrorState({
          type: 'error',
          message: 'Couldn\'t fetch an answer. Please try again.'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Retry handler
  const handleRetry = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setErrorState(null);
    handleSubmit();
  };

  const handleSuggestedQuery = (e, q) => {
    e.preventDefault();
    e.stopPropagation();
    setQuery(q);
  };

  /**
   * Navigate to chart ONLY when user explicitly selects it
   * This is the ONLY place navigation should happen from this component
   */
  const handleChartClick = (e, chartPath) => {
    e.preventDefault();
    e.stopPropagation();
    
    setShowDialog(false);
    
    // Navigate with context state
    navigate(chartPath, { 
      state: { 
        context, 
        filters: buildContext(), 
        chips: contextChips.map(c => c.label),
        from: entryPoint
      }
    });
  };

  /**
   * Open full analytics page - explicit user action
   */
  const handleOpenFullAnalytics = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setShowDialog(false);
    
    navigate('/analytics/qa', { 
      state: { 
        context, 
        filters: buildContext(), 
        chips: contextChips.map(c => c.label),
        from: entryPoint
      }
    });
  };

  const handleClose = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setShowDialog(false);
    setErrorState(null);
    setResponse(null);
  };

  // Button variants
  const buttonContent = (
    <>
      <Sparkles className="w-4 h-4 mr-2" />
      {variant !== 'compact' && 'Ask Analytics'}
    </>
  );

  const buttonClass = variant === 'pill' 
    ? 'rounded-full bg-blue-600 hover:bg-blue-700 text-white' 
    : variant === 'compact'
    ? 'bg-blue-600 hover:bg-blue-700 text-white px-3'
    : 'bg-blue-600 hover:bg-blue-700 text-white';

  return (
    <>
      {/* TRIGGER: Must be button type="button" - no <a> or <Link> */}
      <button
        type="button"
        className={`inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 px-4 py-2 ${buttonClass} ${className}`}
        onClick={handleOpen}
        data-testid={dataTestId}
        data-entry-point={entryPoint}
      >
        {buttonContent}
        {contextChips.length > 0 && variant !== 'compact' && (
          <Badge variant="secondary" className="ml-2 text-xs bg-blue-500">
            {contextChips.length} filters
          </Badge>
        )}
      </button>

      {/* MODAL: Opens in-place, no navigation on mount */}
      <Dialog open={showDialog} onOpenChange={(open) => !open && handleClose()}>
        <DialogContent className="max-w-2xl" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-blue-600" />
              {config.label}
            </DialogTitle>
            <DialogDescription>{config.description}</DialogDescription>
          </DialogHeader>

          {/* RBAC Error - Inline warning, NO redirect */}
          {rbacError && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Access Restricted</p>
                <p className="text-sm text-amber-700 mt-1">{rbacError}</p>
              </div>
            </div>
          )}

          {/* Context Chips */}
          {contextChips.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap p-3 bg-blue-50 rounded-lg">
              <span className="text-xs text-blue-600 font-medium">Context Applied:</span>
              {contextChips.map((chip, idx) => (
                <Badge key={idx} variant="outline" className="text-xs bg-white">
                  {chip.label}
                </Badge>
              ))}
            </div>
          )}

          {/* Query Input */}
          <div className="space-y-3">
            <div className="relative">
              <input
                type="text"
                placeholder="Ask a question about your data..."
                className="w-full px-4 py-3 border rounded-lg pr-12"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSubmit(e)}
                data-testid="analytics-query-input"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 h-auto inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                onClick={handleSubmit}
                disabled={loading || !query.trim()}
              >
                {loading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </button>
            </div>

            {/* Suggested Queries - button type="button" */}
            <div className="flex flex-wrap gap-2">
              <span className="text-xs text-gray-500 w-full">Try asking:</span>
              {effectiveSuggestedQueries.map((q, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={(e) => handleSuggestedQuery(e, q)}
                  className="text-xs px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg text-center" data-testid="qa-loading">
              <RefreshCw className="w-5 h-5 animate-spin text-blue-600 mx-auto mb-2" />
              <p className="text-sm text-blue-700">{loadingMessage}</p>
            </div>
          )}

          {/* Error State */}
          {errorState && (
            <div className="mt-4 p-4 bg-red-50 rounded-lg" data-testid="qa-error">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-red-700">{errorState.message}</p>
                  <button
                    type="button"
                    onClick={handleRetry}
                    className="mt-2 text-xs px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-700 rounded-full transition-colors"
                    data-testid="qa-retry-btn"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Response */}
          {response && !loading && (
            <div className="mt-4 space-y-4" data-testid="qa-response">
              {/* Answer */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-800">{response.answer}</p>
              </div>
              
              {/* Insights Grid - Sprint 24 Enhancement */}
              {response.insights?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Key Findings</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {response.insights.map((insight, idx) => (
                      <div 
                        key={idx} 
                        className={`p-3 rounded-lg border ${
                          insight.severity === 'critical' ? 'bg-red-50 border-red-200' :
                          insight.severity === 'warning' ? 'bg-amber-50 border-amber-200' :
                          'bg-blue-50 border-blue-200'
                        }`}
                        data-testid={`insight-${idx}`}
                      >
                        <div className="flex items-start justify-between">
                          <span className={`text-xs font-medium ${
                            insight.severity === 'critical' ? 'text-red-700' :
                            insight.severity === 'warning' ? 'text-amber-700' :
                            'text-blue-700'
                          }`}>
                            {insight.title}
                          </span>
                          <span className={`text-lg font-bold ${
                            insight.severity === 'critical' ? 'text-red-900' :
                            insight.severity === 'warning' ? 'text-amber-900' :
                            'text-blue-900'
                          }`}>
                            {insight.value}
                          </span>
                        </div>
                        {insight.delta && (
                          <span className={`text-xs ${
                            insight.delta.startsWith('+') ? 'text-red-600' : 'text-green-600'
                          }`}>
                            {insight.delta}
                          </span>
                        )}
                        {insight.where && (
                          <p className="text-xs text-gray-600 mt-1">{insight.where}</p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">{insight.period}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Suggested Charts with Deeplinks - Sprint 24 Enhancement */}
              {response.suggested_charts?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Drill Down</h4>
                  <div className="flex flex-wrap gap-2">
                    {response.suggested_charts.map((chart, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowDialog(false);
                          navigate(chart.deeplink, {
                            state: { context, filters: buildContext(), from: entryPoint }
                          });
                        }}
                        className="inline-flex items-center gap-2 px-3 py-2 text-xs bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-colors"
                        data-testid={`chart-link-${idx}`}
                      >
                        {chart.chart_type === 'line' && <LineChart className="w-4 h-4 text-blue-500" />}
                        {chart.chart_type === 'bar' && <BarChart3 className="w-4 h-4 text-green-500" />}
                        {chart.chart_type === 'pie' && <PieChart className="w-4 h-4 text-purple-500" />}
                        {chart.chart_type === 'dashboard' && <TrendingUp className="w-4 h-4 text-indigo-500" />}
                        {chart.label}
                        <ExternalLink className="w-3 h-3 text-gray-400" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Metadata */}
              <div className="flex items-center justify-between text-xs text-gray-400 pt-2 border-t border-gray-100">
                <div className="flex items-center gap-3">
                  <span>Confidence: {Math.round((response.confidence || 0.8) * 100)}%</span>
                  {response.ai_generated && <Badge variant="outline" className="text-xs">AI Enhanced</Badge>}
                  {response.cached && <Badge variant="secondary" className="text-xs">Cached</Badge>}
                </div>
                <span>{response.latency_ms || 0}ms</span>
              </div>
              
              {/* Assumptions */}
              {response.assumptions?.length > 0 && (
                <p className="text-xs text-gray-400 italic">
                  Assumptions: {response.assumptions.join('; ')}
                </p>
              )}
              
              {/* Legacy chart suggestions fallback */}
              {!response.suggested_charts?.length && response.charts_suggested?.length > 0 && (
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <span className="text-xs text-gray-500">View related charts:</span>
                  {response.charts_suggested.map((chartId, idx) => {
                    const chartOption = CHART_OPTIONS.find(c => c.id === chartId);
                    if (!chartOption) return null;
                    const Icon = chartOption.icon;
                    return (
                      <button
                        key={idx}
                        type="button"
                        onClick={(e) => handleChartClick(e, chartOption.path)}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 border rounded hover:bg-gray-100 transition-colors"
                      >
                        <Icon className="w-3 h-3" />
                        {chartOption.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t">
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleClose}
            >
              Close
            </Button>
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleOpenFullAnalytics}
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Open Full Analytics
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AskAnalyticsButton;
